// Auth token persistence + expiry helpers.
//
// The auth token is persisted in localStorage so the session survives new tabs
// and full page reloads (sessionStorage would scope it to a single tab and bounce
// the user to /login on reload). To avoid keeping a user "logged in" past the
// token's expiry, we decode the JWT `exp` claim and treat an expired token as
// absent everywhere.

const TOKEN_KEY = 'token'

interface JwtPayload {
  exp?: number
  [key: string]: unknown
}

/** Decode a JWT payload without verifying the signature (client-side only). */
function decodeJwt(token: string): JwtPayload | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    // base64url -> base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(json) as JwtPayload
  } catch {
    return null
  }
}

/** True if the token is missing or its `exp` claim is in the past. */
export function isTokenExpired(token: string | null): boolean {
  if (!token) return true
  const payload = decodeJwt(token)
  // If there's no exp claim we can't prove expiry — let the server decide.
  if (!payload || typeof payload.exp !== 'number') return false
  // exp is seconds since epoch; add a small skew allowance.
  return Date.now() >= payload.exp * 1000
}

/** Return the persisted token, clearing + returning null if it has expired. */
export function getValidToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY)
  if (isTokenExpired(token)) {
    if (token) localStorage.removeItem(TOKEN_KEY)
    return null
  }
  return token
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}
